/**
 * Filtro do Painel: refina o que já foi carregado, sem nova busca.
 *
 * Nunca é enviado ao servidor. Filtrar por status no request removeria sensores
 * de dentro do ativo, e tanto o estado consolidado quanto a contagem
 * passariam a ser calculados sobre um subconjunto. Ver CONTEXT.md.
 *
 * Nada é removido da tela: o que casa sobe para o início e é realçado, o que não
 * casa permanece visível, porém esmaecido. Manter tudo preserva a noção de
 * tamanho da frota — um painel que exibisse só o subconjunto faria quem passasse
 * concluir que a frota inteira é aquilo.
 */
import { assetStates, assetStatesBySeverity } from '@/domain/enums/assetState';

/**
 * Estados que links antigos ainda carregam em `?states=`. Falha e Intermitente
 * foram fundidos em Atenção; sem a tradução, uma TV com o link salvo abriria com
 * a frota inteira esmaecida, porque nenhum Ativo casaria com um estado extinto.
 */
const LEGACY_STATES = {
  failure: assetStates.ATTENTION,
  degraded: assetStates.ATTENTION,
};

/**
 * Converte os valores lidos da URL em estados válidos, sem repetição.
 *
 * @param {String[]} values Valores crus do parâmetro `states`.
 * @returns {String[]} Valores de `assetStates`; o que não for estado é descartado.
 */
export function normalizeStateFilter(values) {
  return (values || [])
    .map((value) => LEGACY_STATES[value] || value)
    .filter(
      (value, index, all) => assetStatesBySeverity.indexOf(value) >= 0
        && all.indexOf(value) === index,
    );
}

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** Um ativo casa a busca por qualquer um dos nomes que o identificam. */
function matchesQuery(asset, normalizedQuery) {
  if (!normalizedQuery) {
    return true;
  }

  return [asset.name, asset.installation, asset.company].some(
    (field) => normalize(field).indexOf(normalizedQuery) >= 0,
  );
}

function matchesStates(asset, states) {
  return !states.length || states.indexOf(asset.state) >= 0;
}

/**
 * Ordena casantes primeiro, preservando a ordem original dentro de cada bloco.
 *
 * A comparação por índice mantém a estabilidade sem depender de `Array.sort` ser
 * estável, o que não é garantido nos motores mais antigos do browserslist.
 */
function matchedFirst(groups) {
  return groups
    .map((group, index) => ({ group, index }))
    .sort((left, right) => {
      if (left.group.matched !== right.group.matched) {
        return left.group.matched ? -1 : 1;
      }
      return left.index - right.index;
    })
    .map((entry) => entry.group);
}

/**
 * Marca, ordena e esmaece os cartões conforme o filtro corrente.
 *
 * @param {Object[]} groups Cartões devolvidos por `groupAssets`.
 * @param {Object} [filter] `{ states: String[], query: String }`.
 * @returns {Object} `{ groups, active, matchedGroupCount, matchedAssetCount }`.
 */
export default function applyPanelFilter(groups, filter = {}) {
  const states = filter.states || [];
  const normalizedQuery = normalize(filter.query);
  const active = states.length > 0 || normalizedQuery.length > 0;

  let matchedGroupCount = 0;
  let matchedAssetCount = 0;

  const decorated = (groups || []).map((group) => {
    const assets = group.assets.map((asset) => {
      const matched = active
        && matchesStates(asset, states)
        && matchesQuery(asset, normalizedQuery);

      if (matched) {
        matchedAssetCount += 1;
      }

      return { ...asset, matched, dimmed: active && !matched };
    });

    const matched = assets.some((asset) => asset.matched);
    if (matched) {
      matchedGroupCount += 1;
    }

    return {
      ...group,
      assets,
      matched,
      dimmed: active && !matched,
    };
  });

  return {
    // Sem filtro a ordem estável é preservada, protegendo a memória espacial.
    groups: active ? matchedFirst(decorated) : decorated,
    active,
    matchedGroupCount,
    matchedAssetCount,
  };
}
